import { currencyFormatter } from '../utils/reportingUtils';

export const MARKETING_EMAIL_COLUMNS = [
  { id: 'created_at', label: 'Date', sortable: true, sortType: 'date', align: 'left', first: true, alwaysVisible: true },
  { id: 'name', label: 'Name', sortable: true, sortType: 'text', align: 'left', alwaysVisible: true },
  { id: 'source', label: 'Source', sortable: true, sortType: 'text', align: 'left' },
  { id: 'venue_name', label: 'Venue', sortable: true, sortType: 'text', align: 'left' },
  { id: 'subject', label: 'Subject', sortable: true, sortType: 'text', align: 'left', defaultHidden: true },
  { id: 'delivered', label: 'Delivered', sortable: true, sortType: 'number', align: 'right' },
  { id: 'opened', label: 'Opened', sortable: true, sortType: 'number', align: 'right' },
  { id: 'open_rate', label: 'Open Rate', sortable: true, sortType: 'number', align: 'right' },
  { id: 'clicked', label: 'Clicked', sortable: true, sortType: 'number', align: 'right' },
  { id: 'click_rate', label: 'Click Rate', sortable: true, sortType: 'number', align: 'right' },
  { id: 'conversions', label: 'Conversions', sortable: true, sortType: 'number', align: 'right' },
  { id: 'tickets_sold', label: 'Tickets Sold', sortable: true, sortType: 'number', align: 'right' },
  { id: 'revenue', label: 'Revenue', sortable: true, sortType: 'number', align: 'right' },
];

export const EMAIL_SORT_TYPES = {
  text: { asc: 'A \u2192 Z', desc: 'Z \u2192 A' },
  number: { asc: 'Low \u2192 High', desc: 'High \u2192 Low' },
  date: { asc: 'Oldest first', desc: 'Newest first' },
};

const SOURCE_COLORS = {
  Broadcast: { background: '#f9f5ff', color: '#6941c6', border: '1px solid #d6bbfb' },
  Campaign: { background: '#fef3f2', color: '#b42318', border: '1px solid #fecdca' },
  Workflow: { background: '#fffaeb', color: '#b54708', border: '1px solid #fedf89' },
};

export const EMAIL_CONFIG = {
  sidebarKey: 'marketing_email',
  pageTitle: 'Email',
  pageSubtitle: 'Overview of your email marketing performance.',
  defaultPreset: 'year_to_date',
  filterTitle: 'Filter Email',
  filterSubtitle: 'Apply filters to narrow email data.',
  apiEndpoint: (teamId) => `/teams/${teamId}/reporting/marketing-email/data.json`,

  columns: MARKETING_EMAIL_COLUMNS,
  sortTypes: EMAIL_SORT_TYPES,
  defaultSort: { field: 'created_at', direction: 'desc' },
  localStorageKey: (teamId) => `reportingMarketingEmailColumns_${teamId}`,
  nameColumn: 'name',

  kpiColumns: 3,
  buildKpiCards: (kpis, prior, compactNumber) => [
    {
      top: { label: 'Emails Sent', value: compactNumber(kpis.emails_sent || 0), current: kpis.emails_sent, prior: prior.emails_sent, tooltip: 'Total emails delivered in the period' },
      bottom: { label: 'Emails Opened', value: compactNumber(kpis.emails_opened || 0), current: kpis.emails_opened, prior: prior.emails_opened, tooltip: 'Total emails opened in the period' },
    },
    {
      top: { label: 'Open Rate', value: `${kpis.open_rate || 0}%`, current: kpis.open_rate, prior: prior.open_rate, tooltip: 'Percentage of delivered emails that were opened' },
      bottom: { label: 'Click Rate', value: `${kpis.click_rate || 0}%`, current: kpis.click_rate, prior: prior.click_rate, tooltip: 'Percentage of delivered emails that were clicked' },
    },
    {
      top: { label: 'Emails Clicked', value: compactNumber(kpis.emails_clicked || 0), current: kpis.emails_clicked, prior: prior.emails_clicked, tooltip: 'Total emails clicked in the period' },
      bottom: {
        label: 'Click-to-Open Rate',
        value: kpis.emails_opened > 0 ? `${((kpis.emails_clicked / kpis.emails_opened) * 100).toFixed(1)}%` : '0%',
        current: kpis.emails_opened > 0 ? ((kpis.emails_clicked / kpis.emails_opened) * 100) : 0,
        prior: prior.emails_opened > 0 ? ((prior.emails_clicked / prior.emails_opened) * 100) : 0,
        tooltip: 'Percentage of opened emails that were clicked',
      },
    },
  ],

  engagementTitle: 'Email Engagement Over Time',
  engagementSubtitle: 'Open rate and click rate trends',
  engagementSeries: [
    { dataKey: 'open_rate', label: 'Open Rate', color: '#1982C4' },
    { dataKey: 'click_rate', label: 'Click Rate', color: '#6A4C93' },
  ],
  engagementYFormatter: (v) => `${v}%`,

  secondChartTitle: 'Email Funnel',
  secondChartSubtitle: 'Sent vs opened vs clicked',
  buildFunnelData: (kpis) => [
    { name: 'Sent', value: kpis.emails_sent || 0 },
    { name: 'Opened', value: kpis.emails_opened || 0 },
    { name: 'Clicked', value: kpis.emails_clicked || 0 },
  ],
  funnelHasData: (kpis) => (kpis.emails_sent || 0) > 0,
  funnelLabel: 'emails',

  topPerformersTitle: 'Top Performing Messages',
  topPerformersSubtitle: 'Ranked by open rate',
  buildTopPerformers: (rows) =>
    [...rows]
      .filter((r) => r.delivered > 0 && r.open_rate != null)
      .sort((a, b) => (b.open_rate || 0) - (a.open_rate || 0))
      .slice(0, 10),
  topPerformersSeries: [
    { dataKey: 'open_rate', label: 'Open Rate', color: '#1982C4' },
    { dataKey: 'click_rate', label: 'Click Rate', color: '#6A4C93' },
  ],
  topPerformersYFormatter: (v) => `${v}%`,

  tableTitle: 'Messages',
  searchPlaceholder: 'Search messages...',
  emptyMessage: 'No email message data for the selected period.',
  emptySearchMessage: 'No messages match your search.',
  paginationLabel: 'messages',
  csvFilename: 'marketing_email_messages.csv',

  searchFilter: (r, q) =>
    (r.name || '').toLowerCase().includes(q) ||
    (r.source || '').toLowerCase().includes(q) ||
    (r.venue_name || '').toLowerCase().includes(q) ||
    (r.subject || '').toLowerCase().includes(q),

  renderCellValue: (colId, row, formatDate) => {
    switch (colId) {
      case 'created_at': return formatDate(row.created_at);
      case 'name': return <div style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name || '\u2014'}</div>;
      case 'source': {
        const style = SOURCE_COLORS[row.source] || { background: '#f2f4f7', color: '#344054', border: '1px solid #e4e7ec' };
        return <span className="badge" style={{ ...style, fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 12 }}>{row.source}</span>;
      }
      case 'venue_name': return <div style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.venue_name || '\u2014'}</div>;
      case 'subject': return <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.subject || '\u2014'}</div>;
      case 'delivered': return (row.delivered || 0).toLocaleString();
      case 'opened': return (row.opened || 0).toLocaleString();
      case 'open_rate': return row.open_rate != null ? `${row.open_rate}%` : '\u2014';
      case 'clicked': return (row.clicked || 0).toLocaleString();
      case 'click_rate': return row.click_rate != null ? `${row.click_rate}%` : '\u2014';
      case 'conversions': return (row.conversions || 0).toLocaleString();
      case 'tickets_sold': return (row.tickets_sold || 0).toLocaleString();
      case 'revenue': return currencyFormatter.format(row.revenue || 0);
      default: return '\u2014';
    }
  },

  buildCsvValue: (colId, row) => {
    switch (colId) {
      case 'created_at': return row.created_at ? new Date(row.created_at).toLocaleDateString() : '';
      case 'name': return row.name || '';
      case 'source': return row.source || '';
      case 'venue_name': return row.venue_name || '';
      case 'subject': return row.subject || '';
      case 'delivered': return row.delivered || 0;
      case 'opened': return row.opened || 0;
      case 'open_rate': return row.open_rate != null ? row.open_rate : '';
      case 'clicked': return row.clicked || 0;
      case 'click_rate': return row.click_rate != null ? row.click_rate : '';
      case 'conversions': return row.conversions || 0;
      case 'tickets_sold': return row.tickets_sold || 0;
      case 'revenue': return row.revenue || 0;
      default: return '';
    }
  },

  buildTotals: (totals) => ({
    delivered: (totals.delivered || 0).toLocaleString(),
    opened: (totals.opened || 0).toLocaleString(),
    open_rate: `${totals.open_rate || 0}%`,
    clicked: (totals.clicked || 0).toLocaleString(),
    click_rate: `${totals.click_rate || 0}%`,
    conversions: (totals.conversions || 0).toLocaleString(),
    tickets_sold: (totals.tickets_sold || 0).toLocaleString(),
    revenue: currencyFormatter.format(totals.revenue || 0),
  }),
};
